import React, { ChangeEvent, useState } from 'react';
import styled from 'styled-components';
import {
  Panel,
  Label,
  TextInput,
  FormControl,
  Select,
  Checkbox,
} from '@uhg-harmony/uitk-react';
import { Arrow } from 'builder/scenes/SiteBuilder/SettingsPanel/SettingsPanel';
import {
  SizeLocationData,
  WidgetSizeLocation,
} from '../../../../builder/components/WidgetSizeLocation';
import { Config } from './types';
import { Border } from 'builder/components/WidgetConfigurations/Border';
import {
  BackgroundData,
  Background,
} from 'builder/components/WidgetConfigurations/Background/Background';
import {
  PanelTop,
  StyledPanel,
  StyledPanelTitle,
} from '../../Text/TextWidgetControl';
import {
  FieldWrapper,
  OptionalLabel,
  StyledLabel,
  StyledTextInput,
} from 'builder/components/WidgetConfigurations/TextInput/TextInputPanel';
import { Button } from '@material-ui/core';
import { useDispatch } from 'react-redux';
import { openConditionalEmailDialog } from 'shared/state/ducks/ui';
import ConditionalEmailDialog from '../ConditionalEmail/ConditionalEmailDialog';

const StyledDiv = styled.div`
  display: inline-flex;
  flex-wrap: wrap;
  align-items: center;
`;

const StyledFormDiv = styled.div<{ error: string }>`
  width: 100%;
  display: inline-flex;
  align-items: end;
  margin-top: ${p => (p.error === '' ? '0' : '-1em')};
`;

const StyledCheckbox = styled(Checkbox)`
  margin-top: -0.15rem;
`;

const LabelWrapper = styled.div`
  display: inline-grid;
  align-items: baseline;
  margin-top: 0.5em;
`;

const TimeUnitSelect = styled(Select)`
  margin-top: -0.25em;
  font-family: OptumSans, sans-serif;
  width: 50%;
  border-radius: 0.375rem;
  & > select {
    font-family: OptumSans, sans-serif;
    line-height: 1.25rem;
  }
`;

const StyledTimeInput = styled(TextInput)`
  height: 2.25rem;
  font-family: OptumSans, sans-serif;
`;

const StyledFormControl = styled(FormControl)`
  width: 50%;
  padding-right: 0.5rem;
  #pattern-text-input-err {
    width: 15em;
    display: flex;
    padding: 0;
    font-family: OptumSans, sans-serif;
    & > span {
      font-size: 0.75rem;
      align-items: end;
    }
  }
`;

const StyledCheckboxControl = styled(FormControl)`
  width: auto;
  height: auto;
`;

const SizeLocationPanel = styled(Panel)`
  > div {
    padding: 1rem;
  }
`;

const StyledButton = styled(Button)`
  margin-bottom: 1em;
  .MuiButton-label {
    font-family: OptumSans, sans-serif;
    font-weight: 700;
    text-transform: none;
    font-size: 0.8rem;
  }
`;

export const StyledResponse = styled.div`
  font-family: OptumSans, sans-serif;
  padding: 0.5em;
  border: 1px solid lightgray;
  border-radius: 0.5em;
  font-size: 0.9rem;
`;

const FormWidgetControl: React.FC<WidgetControlProps<Config>> = (
  props: WidgetControlProps<Config>
) => {
  const {
    value,
    onChange,
    widgetState,
    currentPage: { layoutLocked },
  } = props;
  const [email, setEmail] = useState(widgetState.config.emailTo);
  const [formName, setFormName] = useState(widgetState.config.formName);
  const [subject, setSubject] = useState(widgetState.config.subject);
  const [enableConditionalEmail, setEnableConditionalEmail] = useState(
    widgetState.config.conditionalEmailEnabled
  );
  const [timeFrameValue, setTimeFrameValue] = useState(
    widgetState.config.timeFrameValue
  );
  const [timeFrameUnit, setTimeFrameUnit] = useState(
    widgetState.config.timeFrameUnit
  );

  const dispatch = useDispatch();

  const error =
    !/^[1-9][0-9]?$|^100$/.test(timeFrameValue) && timeFrameValue?.length > 0
      ? 'Must be a number between 1-100'
      : '';

  function eligibleFormWidgets() {
    let eligibleWidgets = [];
    const widgets = props.widgetState.children;
    widgets.forEach(child => {
      if (
        props.currentPage.content[child].type === 'Checkbox' ||
        props.currentPage.content[child].type === 'Radio' ||
        props.currentPage.content[child].type === 'Dropdown'
      ) {
        eligibleWidgets.push(props.currentPage.content[child]);
      }
      return eligibleWidgets;
    });
    return eligibleWidgets;
  }

  const updateWidgetSizeLocation = (sizeLoc: SizeLocationData) => {
    onChange({
      ...value,
      width: sizeLoc.width,
      height: sizeLoc.height,
      xLocation: sizeLoc.xLocation,
      yLocation: sizeLoc.yLocation,
      uniqueId: sizeLoc.uniqueId,
    });
  };

  const updateBorderData = (data: BorderData) => {
    onChange({
      ...value,
      borderData: data,
    });
  };

  const updateBackgroundData = (data: BackgroundData) => {
    onChange({
      ...value,
      backgroundData: data,
    });
  };

  const updateBackgroundImage = (data: Image) => {
    onChange({
      ...value,
      backgroundImage: data,
    });
  };

  const handleLineColor = data => {
    onChange({
      ...value,
      lineColor: data,
    });
  };

  const handleFormName = (e: ChangeEvent<HTMLInputElement>) => {
    setFormName(e.target.value);
    onChange({
      ...value,
      formName: e.target.value,
    });
  };

  const handleValues = (e: ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
    onChange({
      ...value,
      emailTo: e.target.value,
    });
  };

  const handleSubject = (e: ChangeEvent<HTMLInputElement>) => {
    setSubject(e.currentTarget.value);
    onChange({
      ...value,
      subject: e.currentTarget.value,
    });
  };

  const handleTimeFrameValue = (e: ChangeEvent<HTMLInputElement>) => {
    setTimeFrameValue(e.currentTarget.value);
    onChange({
      ...value,
      timeFrameValue: e.currentTarget.value,
    });
  };

  const handleTimeFrameUnit = (e: ChangeEvent<HTMLSelectElement>) => {
    setTimeFrameUnit(e.target.value);
    onChange({
      ...value,
      timeFrameUnit: e.target.value,
    });
  };

  const handleBackgroundColor = (data: string) => {
    onChange({
      ...value,
      backgroundData: {
        ...value.backgroundData,
        color: data,
      },
    });
  };

  const handleConditionalEmailCheck = e => {
    setEnableConditionalEmail(e.target.checked);
    onChange({
      ...value,
      conditionalEmailEnabled: e.target.checked,
    });
  };

  const handleEditConditionalEmailClick = () => {
    dispatch(openConditionalEmailDialog());
  };

  const renderEditConditionalLink = () => {
    const linkTitle =
      !widgetState.config.conditionalEmailConfig ||
      widgetState.config.conditionalEmailConfig.length === 0
        ? 'Add Customized E-mail List'
        : 'Edit Customized E-mail List';
    return (
      <>
        <StyledButton
          onClick={handleEditConditionalEmailClick}
          variant="contained"
        >
          {linkTitle}
        </StyledButton>
      </>
    );
  };

  return (
    <>
      <Panel.Group>
        <StyledPanel>
          <Panel
            title={<Label style={StyledPanelTitle}>General</Label>}
            icon={Arrow}
            data-test-id="general-panel"
          >
            <PanelTop>
              <LabelWrapper>
                <StyledLabel>Form Name</StyledLabel>
                <OptionalLabel>
                  (This will be displayed in the form submission center)
                </OptionalLabel>
              </LabelWrapper>
              <StyledTextInput
                data-test-id="form-name"
                value={formName}
                onChange={handleFormName}
              />
              <LabelWrapper>
                <StyledLabel>Form Recipients</StyledLabel>
                <OptionalLabel>
                  (Add comma-separated e-mail addresses)
                </OptionalLabel>
              </LabelWrapper>
              <StyledTextInput
                data-test-id="email-input"
                value={email}
                onChange={handleValues}
              />
              <StyledDiv>
                <LabelWrapper>
                  <StyledLabel>Enable conditional e-mail?</StyledLabel>
                </LabelWrapper>
                <StyledCheckboxControl id="checkbox-controlled">
                  <StyledCheckbox
                    data-test-id="browser-checkbox"
                    checked={enableConditionalEmail}
                    onChange={handleConditionalEmailCheck}
                  />
                </StyledCheckboxControl>
              </StyledDiv>
              {enableConditionalEmail && renderEditConditionalLink()}
              <ConditionalEmailDialog
                formWidget={props.widgetState}
                eligibleFormWidgets={eligibleFormWidgets()}
                conditionalEmailConfig={
                  widgetState.config.conditionalEmailConfig
                }
                siteId={props.site.id}
                currentPage={props.currentPage}
              />
              <LabelWrapper>
                <StyledLabel>Subject Line</StyledLabel>
                <OptionalLabel>
                  (Add content for e-mail subject line)
                </OptionalLabel>
              </LabelWrapper>
              <StyledTextInput
                data-test-id="subject-input"
                value={subject}
                onChange={handleSubject}
              />
              <div style={{ marginBottom: error === '' ? '-1em' : '0' }}>
                <LabelWrapper>
                  <StyledLabel>Response Time Frame</StyledLabel>
                  <OptionalLabel>
                    (Timeline given to your end users in which your team will
                    respond to their form submission)
                  </OptionalLabel>
                </LabelWrapper>
              </div>
              <StyledFormDiv error={error}>
                <StyledFormControl id="pattern-text-input" error={error}>
                  <StyledTimeInput
                    place
                    data-test-id="timeframe-input"
                    value={timeFrameValue}
                    onChange={handleTimeFrameValue}
                  />
                </StyledFormControl>
                <TimeUnitSelect onChange={handleTimeFrameUnit}>
                  <option value="" selected={timeFrameUnit === ''}></option>
                  <option
                    value="hour(s)"
                    selected={timeFrameUnit === 'hour(s)'}
                  >
                    hour(s)
                  </option>
                  <option value="day(s)" selected={timeFrameUnit === 'day(s)'}>
                    day(s)
                  </option>
                  <option
                    value="week(s)"
                    selected={timeFrameUnit === 'week(s)'}
                  >
                    week(s)
                  </option>
                </TimeUnitSelect>
              </StyledFormDiv>
              <FieldWrapper>
                <StyledLabel>Response Preview</StyledLabel>
                <StyledResponse>
                  "You can expect a response from our team within{' '}
                  {timeFrameValue} {timeFrameUnit}."
                </StyledResponse>
              </FieldWrapper>
            </PanelTop>
          </Panel>
        </StyledPanel>
        <StyledPanel>
          <Panel
            title={<Label style={StyledPanelTitle}>Background</Label>}
            initiallyClosed
            icon={Arrow}
            data-test-id="background-panel"
          >
            <Background
              data-test-id="background-component"
              disabled={layoutLocked}
              backgroundData={updateBackgroundData}
              backgroundImage={updateBackgroundImage}
              handleBackgroundColor={handleBackgroundColor}
              widgetState={widgetState}
              site={props.site}
              updateSite={props.updateSite}
              saveSite={props.saveSite}
            />
          </Panel>
        </StyledPanel>
        <StyledPanel>
          <SizeLocationPanel
            title={<Label style={StyledPanelTitle}>Size and Location</Label>}
            initiallyClosed
            icon={Arrow}
            data-test-id="sizeandlocation-panel"
          >
            <WidgetSizeLocation
              data-test-id="widget-size-location"
              disabled={layoutLocked}
              activeWidgetId={props.widgetState.id}
              sizeLocation={{
                xLocation: value.xLocation,
                yLocation: value.yLocation,
                width: value.width,
                height: value.height,
                uniqueId: value.uniqueId,
              }}
              updateWidgetSizeLocation={updateWidgetSizeLocation}
            />
          </SizeLocationPanel>
        </StyledPanel>
        <StyledPanel>
          <Panel
            title={<Label style={StyledPanelTitle}>Border</Label>}
            initiallyClosed
            icon={Arrow}
            data-test-id="border-panel"
          >
            <Border
              data-test-id="border-component"
              updateBorderData={updateBorderData}
              disabled={layoutLocked}
              handleLineColor={handleLineColor}
              widgetState={widgetState}
              pages={props.pages}
              site={props.site}
              updateSite={props.updateSite}
              saveSite={props.saveSite}
            />
          </Panel>
        </StyledPanel>
      </Panel.Group>
    </>
  );
};

export default FormWidgetControl;
